import * as React from 'react';
import { CSSProperties } from 'react';
import { ILazyloaderProps, ILazyloaderState } from './Lazyloader.types';

/**
 * Gets an low quality version of an image in base64 format and downloads the high quality image on page load.
 */
export class Lazyloader extends React.Component<ILazyloaderProps, ILazyloaderState> {
  private highresImage: CSSProperties = {
    backgroundImage: `url(${this.props.imageUrl})`,
  };

  private lowresImage: CSSProperties = {
    backgroundImage: `url(${this.props.imageUrl}?w=600&quality=0)`,
  };

  private lazyloaderRef: HTMLElement;

  constructor(props: ILazyloaderProps) {
    super(props);
    this.state = {
      lowresLoaded: '',
      highresLoaded: '',
      highresImgSrc: '',
      highresStyleValue: null,
    };
  }

  public componentDidMount(): void {
    // Load a low res image from here if we are rendering a background image
    if (this.props.isBackgroundImage) {
      let lowresImg = new Image();
      lowresImg.src = this.props.imageUrl + '?w=600&quality=0';
      lowresImg.onload = () => {
        this.setState({
          lowresLoaded: 'loaded',
        });
      };
    }
    const observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].isIntersecting) {
          observer.unobserve(this.lazyloaderRef);
          this.setLazyLoaderProps();
        }
      },
      { root: null, threshold: 0 }
    );
    observer.observe(this.lazyloaderRef);
  }

  private setLazyLoaderProps() {
    // The size of the container for the image, i.e. the width of the space the image should occupy.
    const size = this.lazyloaderRef.offsetWidth;

    // Take into account user device DPI but cap it at 3.
    let dpi = 1;
    if (window.devicePixelRatio !== undefined) {
      dpi = window.devicePixelRatio;
      if (dpi > 3) {
        dpi = 3;
      }
    }

    // Load the high res image, taking into account size and dpi
    let highresImg = new Image();
    let highresImgSrc = `${this.props.imageUrl}?w=${size * dpi}&quality=90`;
    highresImg.onload = () => {
      this.setState(
        {
          highresImgSrc,
          highresStyleValue: { backgroundImage: `url(${highresImgSrc})` },
        },
        // Callback after the first setState has finished.
        () => {
          // Need to set the highresLoaded state a little bit later than the other states because
          // Chrome does not transition the image properly when the <img> gets its src and classes updated
          // at the same time. We need to update the src first, and then apply the class for the transition
          // to work properly. Simply wrapping it in a setTimeout of 0 ms is enough to solve the problem.
          setTimeout(() => {
            this.setState({
              highresLoaded: 'loaded',
            });
          }, 0);
        }
      );
    };
    // Setting the src to a valid uri will cause an HTTP request 'behind the scenes'.
    highresImg.src = highresImgSrc;
  }

  render() {
    const { isBackgroundImage, isInEditMode, lowresBase64, imageAltText, imageUrl } = this.props;
    const { lowresLoaded, highresLoaded, highresStyleValue, highresImgSrc } = this.state;
    const alt = imageAltText ? imageAltText : '';
    if (isBackgroundImage) {
      return (
        <div
          className="lazyloader lazyloader--background"
          ref={(ref) => {
            this.lazyloaderRef = ref;
          }}>
          <div className={`lazyloader__bgimage lazyloader__lowres ${lowresLoaded}`} style={this.lowresImage} />
          <div className={`lazyloader__bgimage lazyloader__highres ${highresLoaded}`} style={highresStyleValue} />
        </div>
      );
    } else {
      return (
        <div
          className="lazyloader"
          ref={(ref) => {
            this.lazyloaderRef = ref;
          }}>
          <img className={`lazyloader__image lazyloader__lowres loaded`} alt="" src={lowresBase64} />
          <img
            className={`lazyloader__image lazyloader__highres ${isInEditMode ? 'loaded' : ''} ${highresLoaded}`}
            src={isInEditMode ? imageUrl : highresImgSrc}
            alt={alt}
          />
        </div>
      );
    }
  }
}
